import { useState } from 'react';
import {
  gelatoOrderUrl,
  stripeCustomerUrl,
  stripeMode,
  stripePaymentUrl,
  stripeSearchUrl,
} from '../lib/external';

interface OrderRefs {
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  gelato_order_id: string | null;
}

/** "Stripe ↗" / "Gelato ↗" buttons for an order. Clicks don't bubble, so it's safe inside clickable rows. */
export function OrderExternalLinks({
  order,
  compact = false,
  stripeLabel,
}: {
  order: OrderRefs;
  compact?: boolean;
  stripeLabel?: string;
}) {
  const mode = stripeMode(order.stripe_checkout_session_id);
  // The payment page is the useful one; search finds the session when there's no PaymentIntent yet.
  const stripeUrl = order.stripe_payment_intent_id
    ? stripePaymentUrl(order.stripe_payment_intent_id, mode)
    : order.stripe_checkout_session_id
      ? stripeSearchUrl(order.stripe_checkout_session_id, mode)
      : null;

  return (
    <span style={{ display: 'inline-flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
      {stripeUrl && (
        <ExternalButton href={stripeUrl} compact={compact} color="#635bff">
          {stripeLabel ?? 'Stripe'}
          {mode === 'test' && !compact ? ' (test)' : ''}
        </ExternalButton>
      )}
      {order.gelato_order_id && (
        <ExternalButton href={gelatoOrderUrl(order.gelato_order_id)} compact={compact} color="#0f766e">
          Gelato
        </ExternalButton>
      )}
    </span>
  );
}

export function StripeCustomerLink({
  customerId,
  checkoutSessionId,
  label = 'Open in Stripe',
}: {
  customerId: string;
  checkoutSessionId?: string | null;
  label?: string;
}) {
  return (
    <ExternalButton href={stripeCustomerUrl(customerId, stripeMode(checkoutSessionId))} color="#635bff">
      {label}
    </ExternalButton>
  );
}

export function ExternalButton({
  href,
  color,
  compact = false,
  children,
}: {
  href: string;
  color: string;
  compact?: boolean;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: compact ? '2px 8px' : '6px 12px',
        fontSize: compact ? 11 : 13,
        fontWeight: 500,
        color,
        background: '#fff',
        border: `1px solid ${color}55`,
        borderRadius: 6,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {children} ↗
    </a>
  );
}

/** An id with a copy button — the fallback when a dashboard URL can't be built. */
export function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <code style={{ fontSize: 12 }}>{value.length > 28 ? `${value.slice(0, 26)}…` : value}</code>
      <button
        type="button"
        title="Copy"
        onClick={() => {
          navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          });
        }}
        style={{
          border: '1px solid #e5e5e5',
          background: '#fff',
          borderRadius: 4,
          fontSize: 11,
          padding: '1px 6px',
          cursor: 'pointer',
          color: '#6b7280',
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}
